import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import Logo from './Logo';
import { useLang } from '@/context/LanguageContext';
import { scrollToId } from './LenisScroll';

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);

  const links = [
    { id: 'how', label: t.nav.how, testid: 'nav-link-how' },
    { id: 'rates', label: t.nav.rates, testid: 'nav-link-rates' },
    { id: 'faq', label: t.nav.faq, testid: 'nav-link-faq' },
    { id: '/track', label: t.nav.track, testid: 'nav-link-track' },
  ];

  const go = (id) => {
    setOpen(false);
    if (id.startsWith('/')) {
      setTimeout(() => window.location.assign(id), 60);
      return;
    }
    if (window.location.pathname !== '/') {
      setTimeout(() => window.location.assign(`/#${id}`), 60);
      return;
    }
    setTimeout(() => scrollToId(id), 60);
  };

  const langToggle = (
    <div className="flex items-center rounded-full border border-white/10 bg-ink/70 p-1" data-testid="language-toggle">
      {['en', 'hi'].map((l) => (
        <button
          key={l}
          data-testid={`language-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1 font-mono text-xs font-semibold transition-colors ${
            lang === l ? 'bg-pulse text-ink' : 'text-slate-400 hover:text-white'
          }`}
        >
          {l === 'en' ? 'EN' : 'हिं'}
        </button>
      ))}
    </div>
  );

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-ink/80 backdrop-blur-xl">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <button data-testid="nav-logo-button" onClick={() => window.scrollTo({ top: 0 })} aria-label="PayDay India home">
          <Logo />
        </button>

        <div className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="text-sm font-semibold text-slate-300 transition-colors hover:text-pulse"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden items-center gap-4 md:flex">
          {langToggle}
          <button
            data-testid="nav-apply-button"
            onClick={() => go('apply')}
            className="rounded-full bg-pulse px-5 py-2 text-sm font-bold text-ink transition-transform hover:scale-[1.03] hover:bg-pulse-dim glow-pulse"
          >
            {t.nav.apply}
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="rounded-lg border border-white/10 p-2 text-white md:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-white/10 bg-ink/95 px-5 pb-6 pt-4 backdrop-blur-xl md:hidden">
          <div className="flex flex-col gap-4">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`${l.testid}-mobile`}
                onClick={() => go(l.id)}
                className="text-left text-base font-semibold text-slate-200"
              >
                {l.label}
              </button>
            ))}
            <div className="flex items-center justify-between pt-2">
              {langToggle}
              <button
                data-testid="nav-apply-button-mobile"
                onClick={() => go('apply')}
                className="rounded-full bg-pulse px-5 py-2 text-sm font-bold text-ink"
              >
                {t.nav.apply}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
